import { createContext, useContext, useEffect, type MutableRefObject, type RefObject } from 'react'

/**
 * The nearest interactive overlay boundary for custom portals.
 *
 * Radix Modal makes everything outside Dialog.Content inert. A control mounted
 * inside that modal must therefore portal inside the same content boundary;
 * sending it to `document.body` can paint it while making it unclickable. The
 * null fallback preserves standalone controls.
 */
export interface OverlayPortalBoundary {
  target: RefObject<HTMLElement | null>
  /** Number of mounted custom child overlays that currently own Escape. */
  escapeLayers: MutableRefObject<number>
}

export const OverlayPortalContext = createContext<OverlayPortalBoundary | null>(null)

export function useOverlayPortalTarget(): HTMLElement | null {
  return useContext(OverlayPortalContext)?.target.current ?? null
}

/**
 * Claim Escape for the lifetime of a custom child overlay.
 *
 * Radix sees Escape at its dismissable layer before a document listener can
 * reliably stop it. The containing Modal consults this count and withholds its
 * own dismissal; the child still closes itself and remains the only layer that
 * changes. A ref count, rather than a boolean, keeps stacked custom overlays
 * honest.
 */
export function useOverlayEscapeLayer(): void {
  const boundary = useContext(OverlayPortalContext)
  useEffect(() => {
    if (!boundary) return
    boundary.escapeLayers.current += 1
    return () => { boundary.escapeLayers.current -= 1 }
  }, [boundary])
}
